import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import RichText from '../RichText'
import useBrand from '../../hooks/useBrand'
import {
  ReviewComponentVariants,
  ReviewSection as ReviewSectionType,
} from '../../types/landingPageV2'
import getColor from '../../utils/colors'
import { useTrustPilotTaggedReviews } from '../../hooks/useTrustPilotReviews'
import useTrustPilot from '../../hooks/useTrustPilot'
import { TrustpilotReview } from '@talentinc/gatsby-source-trustpilot-widget'
import { Trans } from 'gatsby-plugin-react-i18next'
import Carousel from '../LandingPage/Carousel'
import ReviewCardTitle from './ReviewCardTitle'
import ReviewCardName from './ReviewCardName'
import ReviewCardSimple from './ReviewCardSimple'
import ReviewsCarousel from './ReviewsCarousel'
import { BLOCKS } from '@contentful/rich-text-types'
import formatContentfulClasses from '../../utils/customClasses'
import useHide from '../../hooks/useHide'
import ReviewProgressLine from './ReviewProgressLine'

export interface ReviewSectionProps {
  reviewSection: ReviewSectionType
  sectionClass?: string
}

interface ReviewProps {
  reviewSection: ReviewSectionType
  reviews: TrustpilotReview[]
}

interface ProgressBarContextType {
  setCurrentIndex?: (currentIndex: number) => void
  currentIndex: number
}

interface RichTextBodyItemType {
  nodeType: string
}

export const ProgressBarContext = React.createContext<ProgressBarContextType>({
  currentIndex: 0,
})

const ReviewSection: React.FC<ReviewSectionProps> = ({
  reviewSection,
  sectionClass,
}) => {
  const { classes, cx } = useStyles(reviewSection)
  const brand = useBrand()
  const reviews = useTrustPilotTaggedReviews(
    reviewSection.tag ? reviewSection.tag : 'shown-on-website',
    reviewSection.externalBrand || brand.name
  )
  const trustPilotCount = useTrustPilot()
  const fiveStars = trustPilotCount?.businessUnit?.numberOfReviews?.fiveStars
    ? trustPilotCount.businessUnit?.numberOfReviews.fiveStars
    : 800
  const roundedFiveStars = Math.floor(fiveStars / 50) * 50
  const hideClasses = useHide(reviewSection.hideOn)
  const [reviewProgressIndex, setReviewProgressIndex] = useState(0)

  if (brand.isTopCvFr) {
    return null
  }

  if (
    reviewSection.customReviewComponent ==
    ReviewComponentVariants.ProgressBarCarousel
  ) {
    const body = JSON.parse(reviewSection.body.raw)
    const heading = body.content.find((item: RichTextBodyItemType) =>
      item.nodeType.includes('heading')
    )
    const content = body.content.filter(
      (item: RichTextBodyItemType) => !item.nodeType.includes('heading')
    )
    body.content = content

    return (
      <Box
        className={cx(
          sectionClass,
          classes.container,
          classes.progressBarCarouselContainer,
          ...formatContentfulClasses(reviewSection.codeClasses),
          hideClasses
        )}
      >
        <React.Fragment>
          {heading != undefined && (
            <RichText
              cssEntries={reviewSection.css}
              rawBody={JSON.stringify(heading)}
              options={{
                props: {
                  [BLOCKS.HEADING_1]: {
                    className: classes.header,
                  },
                  [BLOCKS.HEADING_2]: {
                    className: classes.header,
                  },
                  [BLOCKS.PARAGRAPH]: {
                    className: classes.text,
                  },
                },
              }}
              references={reviewSection.body.references}
            />
          )}
          <Box
            className={cx({
              [classes.boxRow]: true,
              // [classes.box]: true,
              [classes.boxSimpleCarousel]: true,
            })}
          >
            <Box className={classes.progressBarCarouselLeft}>
              <RichText
                cssEntries={reviewSection.css}
                rawBody={JSON.stringify(body)}
                options={{
                  props: {
                    [BLOCKS.HEADING_1]: {
                      className: classes.header,
                    },
                    [BLOCKS.HEADING_2]: {
                      className: cx(classes.header),
                    },
                    [BLOCKS.PARAGRAPH]: {
                      className: cx(classes.text),
                    },
                  },
                }}
                references={reviewSection.body.references}
              />
            </Box>
            <Box className={classes.progressBarCarouselRight}>
              <ProgressBarContext.Provider
                value={{
                  currentIndex: reviewProgressIndex,
                  setCurrentIndex: setReviewProgressIndex,
                }}
              >
                <CustomReviewComponent
                  reviews={reviews}
                  reviewSection={reviewSection}
                />
                <ReviewProgressLine
                  currentIndex={reviewProgressIndex}
                  reviewsCount={reviews.length}
                />
              </ProgressBarContext.Provider>
            </Box>
          </Box>
        </React.Fragment>
      </Box>
    )
  }

  return (
    <Box
      className={cx(
        {
          // @ts-ignore
          [sectionClass]: true,
          [classes.container]: true,
          [classes.containerSimpleCarousel]:
            reviewSection.customReviewComponent ===
            ReviewComponentVariants.SimpleCarousel,
        },
        ...formatContentfulClasses(reviewSection.codeClasses),
        hideClasses
      )}
    >
      <React.Fragment>
        {reviewSection.subtitle && (
          <Typography component="h2" className={classes.subtitle}>
            {reviewSection.subtitle}
          </Typography>
        )}
        {reviewSection.body || brand.name === 'TopResume' ? (
          <Box
            className={cx({
              [classes.box]: true,
              [classes.boxSimpleCarousel]:
                reviewSection.customReviewComponent ===
                ReviewComponentVariants.SimpleCarousel,
            })}
          >
            <RichText
              cssEntries={reviewSection.css}
              rawBody={reviewSection.body.raw}
              options={{
                props: {
                  [BLOCKS.HEADING_1]: {
                    className: classes.header,
                  },
                  [BLOCKS.HEADING_2]: {
                    className: cx(classes.header),
                  },
                  [BLOCKS.PARAGRAPH]: {
                    className: cx(classes.text),
                  },
                },
              }}
              references={reviewSection.body.references}
            />
          </Box>
        ) : (
          <Typography
            variant={'h2'}
            className={cx({
              [classes.title]: true,
              [classes.boxSimpleCarousel]:
                reviewSection.customReviewComponent ===
                ReviewComponentVariants.SimpleCarousel,
            })}
          >
            <Trans
              i18nKey="trustpilot.fiveStarsReviews"
              values={{
                reviews: roundedFiveStars,
              }}
            />
          </Typography>
        )}
      </React.Fragment>
      <CustomReviewComponent reviews={reviews} reviewSection={reviewSection} />
    </Box>
  )
}

const CustomReviewComponent: React.FC<ReviewProps> = ({
  reviewSection,
  reviews,
}) => {
  const { classes, cx } = useStyles(reviewSection)
  const brand = useBrand()

  switch (reviewSection.customReviewComponent) {
    case ReviewComponentVariants.SimpleCarousel:
    case ReviewComponentVariants.ProgressBarCarousel:
      return (
        <ReviewsCarousel variant={reviewSection.customReviewComponent}>
          {reviews.map((review) => (
            <ReviewCardTitle
              key={review.title}
              variableWidth={
                reviewSection.customReviewComponent ===
                ReviewComponentVariants.ProgressBarCarousel
              }
              review={review}
              background={reviewSection.reviewBackgroundColor}
            />
          ))}
        </ReviewsCarousel>
      )
    case ReviewComponentVariants.MasonryCarousel:
      return (
        <Box className={classes.carousel}>
          <Carousel
            slidesToShow={3}
            arrows={false}
            dots={false}
            rows={2}
            autoplay={true}
            autoplaySpeed={4000}
            responsiveSettings={responsiveSettings}
          >
            {reviews.map((review) => (
              <ReviewCardName
                key={review.title}
                review={review}
                background={reviewSection.reviewBackgroundColor}
              />
            ))}
          </Carousel>
        </Box>
      )
    case ReviewComponentVariants.FullWidthCarousel:
      return (
        <Box
          className={cx(classes.carousel, {
            [classes.carouselNarrow]:
              brand.name === 'TopResume' || brand.name === 'TopCV',
          })}
        >
          <Carousel
            slidesToShow={1}
            // @ts-ignore
            dots={true}
            rows={1}
            autoplay={true}
            autoplaySpeed={4000}
          >
            {reviews.map((review) => (
              <ReviewCardSimple
                key={review.title}
                review={review}
                background={reviewSection.reviewBackgroundColor}
              />
            ))}
          </Carousel>
        </Box>
      )
    default:
      return null
  }
}

const responsiveSettings = [
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 600,
    settings: {
      slidesToShow: 1,
    },
  },
]

const useStyles = makeStyles<ReviewSectionType>()((theme, reviewSection) => {
  const containerBgColor = getColor(reviewSection?.backgroundColor, theme)

  return {
    box: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
    boxRow: {
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    headerTitle: {
      textAlign: 'center',
    },
    boxSimpleCarousel: {
      alignItems: 'flex-start',
      maxWidth: 'calc(100% - 5em)',
      [theme.breakpoints.down('md')]: {
        alignItems: 'center',
        maxWidth: '100%',
      },
    },
    subtitle: {
      color: theme.palette.secondary.dark,
      textTransform: 'uppercase',
      fontSize: '.8rem',
      letterSpacing: '.9px',
      fontWeight: 'bold',
      marginBottom: '.7rem',
      [theme.breakpoints.down('md')]: {
        marginBottom: '.7rem',
      },
    },
    container: {
      alignItems: 'center',
      backgroundColor: containerBgColor,
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
      paddingBottom: '3.25em',
      paddingTop: '4.19em',
    },
    containerSimpleCarousel: {
      alignItems: 'flex-start',
    },
    carousel: {
      width: '100%',

      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        width: '90vw',
      },
    },
    carouselNarrow: {
      width: '90%',
    },
    title: {
      [theme.breakpoints.down('md')]: {
        fontSize: '1.81em',
        lineHeight: '1.312',
      },
    },
    narrow: {
      padding: '0.5625rem 16%',
      [theme.breakpoints.down('md')]: {
        padding: '0.34375rem 1rem',
      },
    },
    narrowPara: {
      padding: '0.5625rem 30%',
      [theme.breakpoints.down('md')]: {
        padding: '0.34375rem 1rem',
      },
    },
    header: {
      [theme.breakpoints.down('md')]: {
        fontSize: '1.8125rem',
        lineHeight: 1.312,
      },
    },
    headerTR: {
      paddingBottom: '.5em',
    },
    text: {
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
      },
    },
    progressBarCarouselLeft: {
      width: '25%',
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    progressBarCarouselRight: {
      width: '75%',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    progressBarCarouselContainer: {
      paddingBottom: '4rem',
      [theme.breakpoints.down('xs')]: {
        paddingRight: '0 !important',
        paddingBottom: '2rem',
        paddingTop: '0',
        '& > h2': {
          marginRight: '6%',
        },
      },
      '& .slick-slide > div > div': {
        height: 'unset',
        margin: '0 24px 0 0',
      },
    },
  }
})

export default ReviewSection
